<template>
	<div class="trigger">
		<button
			ref="triggerButton"
			type="button"
			class="trigger__button"
			:aria-label="languageLabel"
			:aria-controls="menuId"
			:aria-expanded="isOpen ? 'true' : 'false'"
			@click.stop="toggle"
		>
			{{ currentLanguage }}
			<Icon
				icon-name="down-arrow"
				class="trigger__icon"
				:class="{'is-open' : isOpen}"
				size="xs"
			/>
		</button>
		<div
			:id="menuId"
			class="lang-switcher"
			:class="{'is-open' : isOpen}"
			:aria-hidden="isOpen ? 'false' : 'true'"
			:inert="isOpen ? null : ''"
			data-testid="lang-switcher"
		>
			<a
				v-for="lang in languages"
				:key="lang.name"
				class="lang-switcher__item"
				:href="lang.link"
				rel="nofollow"
				data-testid="lang-switcher-item"
				@click="handleClick($event, lang.code)"
			>
				{{ lang.name }}
			</a>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon';
import { mapGetters, mapActions } from 'vuex';

export default {
	name:       'LangSwitcher',
	components: { Icon },
	props:      {
		languages: {
			type:     Array,
			required: true,
		},
		currentLanguage: {
			type:     String,
			required: true,
		},
	},
	data() {
		return {
			isOpen: false,
		};
	},
	computed: {
		...mapGetters({
			isLoggedIn: 'user/isLoggedIn',
		}),
		menuId() {
			return `language-menu-${this._uid}`;
		},
		languageLabel() {
			const label = this.$Helpers.ucfirst(this.$I18n.trans_choice('commons.language', 1));
			return `${label}: ${this.currentLanguage}`;
		},
	},
	watch: {
		isOpen(value) {
			document[(value ? 'add' : 'remove') + 'EventListener']('click', this.onClickOutside);
			window[(value ? 'add' : 'remove') + 'EventListener']('keydown', this.handleKeyDown);
		},
	},
	beforeDestroy() {
		document.removeEventListener('click', this.onClickOutside);
		window.removeEventListener('keydown', this.handleKeyDown);
	},
	methods: {
		...mapActions({
			setLocale: 'user/setLocale',
		}),
		toggle() {
			this.isOpen = !this.isOpen;
		},
		close({ returnFocus = false } = {}) {
			this.isOpen = false;
			if (returnFocus) {
				this.$nextTick(() => this.$refs.triggerButton?.focus());
			}
		},
		onClickOutside(evt) {
			if (!this.$el.contains(evt.target)) {
				this.close();
			}
		},
		handleKeyDown(event) {
			if (event.key === 'Escape') {
				event.stopPropagation();
				this.close({ returnFocus: true });
			}
		},
		handleClick(event, lang) {
			if (this.isLoggedIn) {
				// Since most arena pages are session-less, the locale needs to be set via profile update
				event.stopPropagation();
				event.preventDefault();
				this.setLocale(lang);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.trigger {
	position: relative;
	color: inherit;
}

.trigger__button {
	display: flex;
	align-items: center;
	gap: $sp1;
	color: inherit;
	cursor: pointer;
}

.trigger__icon {
	&.is-open {
		transform: rotate(180deg);
	}
}

.lang-switcher {
	display: none;
	position: absolute;
	bottom: 100%;
	right: 0;
	background-color: var(--color-white);

	&.is-open {
		display: block;
	}

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		background-color: var(--color-grey-950);
	}
}

.lang-switcher__item {
	display: inline-block;
	white-space: nowrap;
	padding: $sp4 $sp5;
	width: 100%;
	color: var(--color-black);

	&:not(:last-child) {
		border-bottom: 1px solid var(--color-grey-300);
	}

	&:hover,
	&:focus {
		background-color: var(--color-grey-100);

		body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			background-color: var(--color-grey-900);
		}
	}
}
</style>
